import { getSupabase } from '$db/remote/supabase';
import { getActiveUserId } from '$core/identity/active-user';
import { nowIso } from '../repositories/common';
import { recoverSyncingOps } from './recovery';
import { enforceCapacity } from './capacity';
import { pushPendingOps } from './push';
import { pullRemote } from './pull';
import { mergeRemote } from './merge';
import { ALL_ENTITY_TYPES } from './tables';
import {
  getSyncMeta,
  setSyncMeta,
  lastPullCursorKey,
  lastPushAtKey
} from '../repositories/sync-meta.repo';

export interface SyncResult {
  ok: boolean;
  pushed: number;
  pulled: number;
  inserted: number;
  updated: number;
  conflicts: number;
  quarantined: number;
  error: string | null;
}

/**
 * Orquestador del ciclo de sync (M4.3).
 * Orden estricto: recovery → capacity → push → pull → merge.
 * Push antes de pull para evitar eco de cambios recién bajados.
 */
export async function runSync(): Promise<SyncResult> {
  const client = getSupabase();
  if (!client) {
    return {
      ok: false,
      pushed: 0,
      pulled: 0,
      inserted: 0,
      updated: 0,
      conflicts: 0,
      quarantined: 0,
      error: 'supabase_not_configured'
    };
  }

  const userId = getActiveUserId();

  try {
    await recoverSyncingOps();
    await enforceCapacity();

    const pushResult = await pushPendingOps(client, userId);

    if (pushResult.networkError) {
      return {
        ok: false,
        pushed: pushResult.applied,
        pulled: 0,
        inserted: 0,
        updated: 0,
        conflicts: 0,
        quarantined: pushResult.quarantined,
        error: pushResult.networkError
      };
    }

    // Cargar el cursor independiente de cada tabla
    const cursors: Record<string, string> = {};
    for (const entityType of ALL_ENTITY_TYPES) {
      cursors[entityType] = (await getSyncMeta<string>(lastPullCursorKey(userId, entityType))) ?? '1970-01-01T00:00:00.000Z';
    }

    const pullResult = await pullRemote(client, userId, cursors);
    const mergeStats = await mergeRemote(pullResult.rows, userId);

    // Guardar el nuevo cursor de cada tabla
    for (const [entityType, newCursor] of Object.entries(pullResult.newCursors)) {
      await setSyncMeta(lastPullCursorKey(userId, entityType), newCursor);
    }
    await setSyncMeta(lastPushAtKey(userId), nowIso());

    return {
      ok: true,
      pushed: pushResult.applied,
      pulled: pullResult.rows.length,
      inserted: mergeStats.inserted,
      updated: mergeStats.updated,
      conflicts: mergeStats.conflicts,
      quarantined: pushResult.quarantined,
      error: null
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      ok: false,
      pushed: 0,
      pulled: 0,
      inserted: 0,
      updated: 0,
      conflicts: 0,
      quarantined: 0,
      error: message
    };
  }
}